import { Theme } from "@mui/material/index";
import { CBAddress, CBAddressType, CBConsentCompanyCode, CBContactDetails, CBContactPersonFormFields, CBCountry, CBCountryExtended, CBDigitalIdentityState, CBDocumentStatusValue, CBEditButtonTrigger, CBEditButtonTriggerStatus, CBExtraAddressDeltaFields, CBGoogleAutocompleteType, CBOmissionType, CBPartnerType, CBTenants, CBValuableLength, CBYesNoType, CustomerBaseDto, IEditCustomerBaseFieldsParams, IFormValues, IMapField, IOSapEnum, IT_FISCAL_CODE_FORMAT_REGEX } from "./types";
import dayjs from "dayjs";

export const getDirtyValues = (
    dirtyFields: any,
    values: any
): any => {

    if (dirtyFields === true || Array.isArray(dirtyFields)) {
        return values;
    }

    return Object.fromEntries(
        Object.keys(dirtyFields).map((key) => [
            key,
            getDirtyValues(dirtyFields[key], values[key])
        ])
    );
}

export const isDirty = (dirtyFields: any, k: string|null): boolean => {
    let a: boolean = false;
    Object.entries(dirtyFields).map(([key, value])=>{
        if(key === k || k == null){
            if(Array.isArray(value)) {
                value.forEach((item: any) => {
                    if(Object.values(item).some((val) => val as boolean)){
                        a=true;
                    }
                });
            } else {
                Object.entries(value as any).map(([key, val]) => {
                    if(Array.isArray(val)) {
                        val.forEach((item: any) => {
                            if(Object.values(item).some((vall) => vall as boolean)){
                                a=true;
                            }
                        });
                    }
                    else {
                        let v = val as boolean;
                        if(v) {
                            a = v;
                            return;
                        }
                    }    
                });
            }
        }
    });
    return a;
}

export const getResidenceAddress = (contactInfo: CBContactDetails | undefined) => {
    return contactInfo?.addresses?.filter((address: CBAddress) => address.addressType == CBAddressType.RESIDENCE)
        .map((residence: CBAddress) => ({
            id: residence.id || null,
            addressNumber: residence.addressNumber || null,
            street: residence.street || null,
            houseNumber: residence.houseNumber || null,
            zipCode: residence.zipCode || null,
            province: residence.province || null,
            city: residence.city || null,
            country: residence.country || null,
            addressType: residence.addressType
        }))[0] || undefined;
}

export const getInvoicingAddress = (contactInfo: CBContactDetails | undefined) => {
    return contactInfo?.addresses?.filter((address: CBAddress) => address.addressType == CBAddressType.INVOICING)
        .map((invoicing: CBAddress) => ({
            id: invoicing.id || null,
            addressNumber: invoicing.addressNumber || null,
            street: invoicing.street || null,
            houseNumber: invoicing.houseNumber || null,
            zipCode: invoicing.zipCode || null,
            province: invoicing.province || null,
            city: invoicing.city || null,
            country: invoicing.country || null,
            addrEmail: invoicing.addrEmail || null,
            pec: invoicing.pec || null,
            addrMobile: invoicing.addrMobile || null,
            telephone: invoicing.telephone || null,
            fax: invoicing.fax || null,
            website: invoicing.website || null,
            companyFiscalCode: invoicing.companyFiscalCode || null,
            recipientCode: invoicing.recipientCode || null,
            addressType: invoicing.addressType
        }))[0] || undefined;
}

export const extractGooglePlaceLocalityLongName = (place: any) => {
    return place?.address_components?.find((p: any) => p.types?.includes(CBGoogleAutocompleteType.LOCALITY) || p.types?.includes(CBGoogleAutocompleteType.LOCALITY2))?.long_name;
}

export const extractGooglePlaceTypeLongName = (place: any, type: string) => {
    return place?.address_components?.find((p: any) => p.types?.includes(type))?.long_name;
}

export const extractGooglePlaceTypeShortName = (place: any, type: string) => {
    return place?.address_components?.find((p: any) => p.types?.includes(type))?.short_name;
}

export const downloadFile = (base64: string, fileName?: string) => {

    const downloadLink = document.createElement('a');
    downloadLink.href = `data:${base64.charAt(0)};base64,` + base64;
    downloadLink.download = fileName ? fileName : 'file';
    downloadLink.click();
}

export const getErrorKey = (err: any) => {
    return err?.response?.data?.code ? err.response.data.code : "default";
}

export const getDocumentStatusChipColor = (status: string | null, theme: Theme) => {

    var color = theme.palette.grey.A400;

    if(!!status)
        switch(status.toUpperCase()) {
            case CBDocumentStatusValue.TO_VALIDATE:
                color = theme.palette.secondary.main;
                break;
            case CBDocumentStatusValue.NOT_TO_VALIDATE:
                color = theme.palette.grey.A400;
                break;
            case CBDocumentStatusValue.IN_PROGRESS:
                color = theme.palette.grey.A400;
                break;
            case CBDocumentStatusValue.COMPLETE:
                color = theme.palette.success.main;
                break;
            case CBDocumentStatusValue.INCOMPLETE:
                color = theme.palette.error.main;
                break;
            case CBDocumentStatusValue.UPDATED:
                color = theme.palette.secondary.main;
                break;
            case CBDocumentStatusValue.DELETED:
                color = theme.palette.grey.A400;
                break;
            case CBDocumentStatusValue.EXPIRED:
                color = theme.palette.error.main;
                break;
            default:
                color = theme.palette.grey.A400
        }

    return color;
}

export const getCustomChipColor = (value: string | null, theme: Theme) => {
    var backgroundColorValue: string | undefined = theme.palette.grey.A400;
    var colorValue: string | undefined = "white";

    if(!!value)
        switch(value.toUpperCase()) {
            case CBDocumentStatusValue.COMPLETE:
            case CBDigitalIdentityState.FULL:
                backgroundColorValue = theme.palette.success.main;
                break;
            case CBDocumentStatusValue.TO_VALIDATE:
            case CBDocumentStatusValue.INCOMPLETE:
            case CBDocumentStatusValue.UPDATED:
            case CBDigitalIdentityState.FULL_PENDING:
            case CBDigitalIdentityState.LIGHT:
            case CBDigitalIdentityState.LIGHT_PENDING:
            case CBDigitalIdentityState.SUPER_LIGHT:
                backgroundColorValue = theme.palette.warning.transparent;
                colorValue = theme.palette.primary.main;
                break;
            default:
                backgroundColorValue = theme.palette.grey.A400
        }
    
    return {color: colorValue, backgroundColor: backgroundColorValue};
}

export const isValidFileType = (file: ArrayBuffer, offset = 0) => {
    const buffers = file.slice(0,8);
    const uint8Array = new Uint8Array(buffers);
    const headersPNG = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];
    const headersJPG_JPEG = [0xff, 0xd8, 0xff];
    return headersPNG.every((header, index) => header === uint8Array[offset+index]) || headersJPG_JPEG.every((header, index) => header === uint8Array[offset+index]);
}

export const isTaxCodeFormatValid = (taxCode: string) => {
    return !!taxCode.toUpperCase().match(IT_FISCAL_CODE_FORMAT_REGEX);
}

export const isVatNumberValid = (vatNumber: string, country?: string) => {
    if(country == CBCountry.ITALY)
        return !!vatNumber.match(/^[0-9]{11}$/);
    
    return !!vatNumber.match(/^[0-9A-Za-z]{5,}$/);
}

export const filterArray = (original: IMapField[], excludedKeys: string[]) => {
    
    if(original?.length == 0)
        return [];
    return original.filter(el => !excludedKeys.includes(el.key)) || [];
}

export const getCBEditButtonTriggerName = (object: CBEditButtonTrigger) => {
    
    for (const key in object) {
        if(object.hasOwnProperty(key) && object[key] == CBEditButtonTriggerStatus.TO_UPDATE)
            return key;
    }
    return null;

}

export const extraAddressDtoToForm = (customer: CustomerBaseDto) => {
    return customer?.linkedAddresses?.map((elem) => ({
        idAssociation: elem.idAssociation || null,
        customerId: elem.customerId || null,
        bpCode: elem.bpCode || null,
        division: elem.division || CBOmissionType.OMISSION_DASH,
        businessName: elem.businessName || CBOmissionType.OMISSION_DASH,
        vatNumber: elem.vatNumber || CBOmissionType.OMISSION_DASH,
        firstName: elem.firstName || CBOmissionType.OMISSION_DASH,
        lastName: elem.lastName || CBOmissionType.OMISSION_DASH,
        taxCode: elem.taxCode || CBOmissionType.OMISSION_DASH,
        addressNumber: elem.addressNumber || CBOmissionType.OMISSION_DASH,
        street: elem.street || CBOmissionType.OMISSION_DASH,
        houseNumber: elem.houseNumber || CBOmissionType.OMISSION_DASH,
        zipCode: elem.zipCode || CBOmissionType.OMISSION_DASH,
        province: elem.province || CBOmissionType.OMISSION_DASH,
        city: elem.city || CBOmissionType.OMISSION_DASH,
        country: elem.country || CBOmissionType.OMISSION_DASH,
        addrEmail: elem.addrEmail || CBOmissionType.OMISSION_DASH,
        pec: elem.pec || CBOmissionType.OMISSION_DASH,
        addrMobile: elem.addrMobile || CBOmissionType.OMISSION_DASH,
        telephone: elem.telephone || CBOmissionType.OMISSION_DASH,
        fax: elem.fax || CBOmissionType.OMISSION_DASH,
        website: elem.website || CBOmissionType.OMISSION_DASH,
        companyFiscalCode: elem.companyFiscalCode || CBOmissionType.OMISSION_DASH,
        recipientCode: elem.recipientCode || CBOmissionType.OMISSION_DASH,
        addressType: elem.addressType || CBOmissionType.OMISSION_DASH,
        product: elem.product || CBOmissionType.OMISSION_DASH,
        productDetail: elem.productDetail || CBOmissionType.OMISSION_DASH,
        registration: elem.registration || CBOmissionType.OMISSION_DASH
    }));
}

export const getExtraAddressForm = (customer: CustomerBaseDto) => {

    const address = getInvoicingAddress(customer.contactInfo);
    const vatNumberCountry = customer.registry?.vatNumberCountry;
    const vatNumber = customer.registry?.vatNumber;
    const vtn = !!vatNumberCountry && !!vatNumber ? (vatNumberCountry + vatNumber) : (!!vatNumber ? vatNumber : '');
    
    const isFranceCountry = address?.country == CBCountry.FRANCE;

    return {
        idAssociation: null,
        customerId: customer.cbCustomerId || null,
        bpCode: customer.bpCode || null,
        division: CBOmissionType.OMISSION_DASH,
        businessName: customer.registry?.businessName || CBOmissionType.OMISSION_DASH,
        vatNumber: vtn || CBOmissionType.OMISSION_DASH,
        firstName: customer.registry?.firstName || CBOmissionType.OMISSION_DASH,
        lastName: customer.registry?.lastName || CBOmissionType.OMISSION_DASH,
        taxCode: customer.registry?.taxCode || CBOmissionType.OMISSION_DASH,
        addressNumber: address?.addressNumber || CBOmissionType.OMISSION_DASH,
        street: address?.street || CBOmissionType.OMISSION_DASH,
        houseNumber: address?.houseNumber || CBOmissionType.OMISSION_DASH,
        zipCode: address?.zipCode || CBOmissionType.OMISSION_DASH,
        province: address?.province || CBOmissionType.OMISSION_DASH,
        city: address?.city || CBOmissionType.OMISSION_DASH,
        country: address?.country || CBOmissionType.OMISSION_DASH,
        addrEmail: address?.addrEmail || CBOmissionType.OMISSION_DASH,
        pec: isFranceCountry ? '' : address?.pec || CBOmissionType.OMISSION_DASH,
        addrMobile: address?.addrMobile || CBOmissionType.OMISSION_DASH,
        telephone: address?.telephone || CBOmissionType.OMISSION_DASH,
        fax: address?.fax || CBOmissionType.OMISSION_DASH,
        website: address?.website || CBOmissionType.OMISSION_DASH,
        companyFiscalCode: address?.companyFiscalCode || CBOmissionType.OMISSION_DASH,
        recipientCode: isFranceCountry ? '' : address?.recipientCode || CBOmissionType.OMISSION_DASH,
        addressType: address?.addressType || CBOmissionType.OMISSION_DASH,
        product: CBOmissionType.OMISSION_DASH,
        productDetail: CBOmissionType.OMISSION_DASH,
        registration: CBOmissionType.OMISSION_DASH
    }

}
    
export const customerFormToEditCustomerParams = (form: IFormValues, contactPersonToDeleteArray: CBContactPersonFormFields[] | [], extraAddressesDelta: CBExtraAddressDeltaFields[] | []) : IEditCustomerBaseFieldsParams => {
    
    const cbCustomerId = form.cbDetails.cbCustomerId;
    const email = form.cbDetails.email;
    const mobilePrefix = form.cbDetails.mobilePrefix == CBOmissionType.OMISSION_DASH ? '' : form.cbDetails.mobilePrefix;
    const mobile = form.cbDetails.mobile;
    const taxCode = form.cbPersonalInfo.taxCode;
    const isOrganization = form.cbDetails.partnerType == CBPartnerType.ORGANIZATION;

    var addresses: any[] = [];

    if(!(form.cbPersonalInfo.residenceStreet == CBOmissionType.OMISSION_DASH && form.cbPersonalInfo.residenceHouseNumber == CBOmissionType.OMISSION_DASH && form.cbPersonalInfo.residenceZipCode == CBOmissionType.OMISSION_DASH && form.cbPersonalInfo.residenceProvince == CBOmissionType.OMISSION_DASH
        && form.cbPersonalInfo.residenceCity == CBOmissionType.OMISSION_DASH && form.cbPersonalInfo.residenceCountry == CBOmissionType.OMISSION_DASH)) 
    {
        addresses.push({
            id: form.cbPersonalInfo.residenceAddressId || null,
            cbCustomerId: cbCustomerId,
            addressNumber: form.cbPersonalInfo.residenceAddressNumber || null,
            street: form.cbPersonalInfo.residenceStreet == CBOmissionType.OMISSION_DASH ? null : form.cbPersonalInfo.residenceStreet,
            houseNumber: form.cbPersonalInfo.residenceHouseNumber == CBOmissionType.OMISSION_DASH ? null : form.cbPersonalInfo.residenceHouseNumber,
            zipCode: form.cbPersonalInfo.residenceZipCode == CBOmissionType.OMISSION_DASH ? null : form.cbPersonalInfo.residenceZipCode,
            province: form.cbPersonalInfo.residenceProvince == CBOmissionType.OMISSION_DASH ? null : form.cbPersonalInfo.residenceProvince || undefined,
            city: form.cbPersonalInfo.residenceCity == CBOmissionType.OMISSION_DASH ? null : form.cbPersonalInfo.residenceCity,
            country: form.cbPersonalInfo.residenceCountry == CBOmissionType.OMISSION_DASH ? null : form.cbPersonalInfo.residenceCountry,
            addrEmail: email == CBOmissionType.OMISSION_DASH ? undefined : email,
            addrMobile: mobile == CBOmissionType.OMISSION_DASH && !mobilePrefix ? undefined : (mobilePrefix + mobile),
            addressType: form.cbPersonalInfo.residenceAddressType
        });
    }

    if(!(form.cbInvoicingAddress.invoicingStreet == CBOmissionType.OMISSION_DASH && form.cbInvoicingAddress.invoicingHouseNumber == CBOmissionType.OMISSION_DASH && form.cbInvoicingAddress.invoicingZipCode == CBOmissionType.OMISSION_DASH
        && form.cbInvoicingAddress.invoicingProvince == CBOmissionType.OMISSION_DASH && form.cbInvoicingAddress.invoicingCity == CBOmissionType.OMISSION_DASH && form.cbInvoicingAddress.invoicingCountry == CBOmissionType.OMISSION_DASH 
        && form.cbInvoicingAddress.invoicingAddrEmail == CBOmissionType.OMISSION_DASH && form.cbInvoicingAddress.invoicingPec == CBOmissionType.OMISSION_DASH /*&& form.cbInvoicingAddress.invoicingAddrMobile == CBOmissionType.OMISSION_DASH*/
        && form.cbInvoicingAddress.invoicingTelephone == CBOmissionType.OMISSION_DASH && form.cbInvoicingAddress.invoicingFax == CBOmissionType.OMISSION_DASH && form.cbInvoicingAddress.invoicingWebsite == CBOmissionType.OMISSION_DASH
        && form.cbInvoicingAddress.invoicingRecipientCode == CBOmissionType.OMISSION_DASH)) 
    {   
        const isFranceInvoicingCountry = form?.cbInvoicingAddress?.invoicingCountry == CBCountry.FRANCE;
        addresses.push({
                id: form.cbInvoicingAddress.invoicingAddressId || null,
                cbCustomerId: cbCustomerId || null,
                addressNumber: form.cbInvoicingAddress.invoicingAddressNumber || null,
                street: form.cbInvoicingAddress.invoicingStreet == CBOmissionType.OMISSION_DASH ? null : form.cbInvoicingAddress.invoicingStreet,
                houseNumber: form.cbInvoicingAddress.invoicingHouseNumber == CBOmissionType.OMISSION_DASH ? null : form.cbInvoicingAddress.invoicingHouseNumber,
                zipCode: form.cbInvoicingAddress.invoicingZipCode == CBOmissionType.OMISSION_DASH ? null : form.cbInvoicingAddress.invoicingZipCode || undefined,
                province: form.cbInvoicingAddress.invoicingProvince == CBOmissionType.OMISSION_DASH ? null : form.cbInvoicingAddress.invoicingProvince || undefined,
                city: form.cbInvoicingAddress.invoicingCity == CBOmissionType.OMISSION_DASH ? null : form.cbInvoicingAddress.invoicingCity,
                country: form.cbInvoicingAddress.invoicingCountry == CBOmissionType.OMISSION_DASH ? null : form.cbInvoicingAddress.invoicingCountry,
                addrEmail: form.cbInvoicingAddress.invoicingAddrEmail == CBOmissionType.OMISSION_DASH ? null : form.cbInvoicingAddress.invoicingAddrEmail,
                pec: isFranceInvoicingCountry ? undefined : (form.cbInvoicingAddress.invoicingPec == CBOmissionType.OMISSION_DASH ? null : form.cbInvoicingAddress.invoicingPec || undefined),
                addrMobile: mobile == CBOmissionType.OMISSION_DASH && !mobilePrefix ? undefined : (mobilePrefix + mobile),
                telephone: form.cbInvoicingAddress.invoicingTelephone == CBOmissionType.OMISSION_DASH ? null : form.cbInvoicingAddress.invoicingTelephone,
                fax: form.cbInvoicingAddress.invoicingFax == CBOmissionType.OMISSION_DASH ? null : form.cbInvoicingAddress.invoicingFax,
                website: form.cbInvoicingAddress.invoicingWebsite == CBOmissionType.OMISSION_DASH ? null : form.cbInvoicingAddress.invoicingWebsite,
                companyFiscalCode: isOrganization && !!taxCode && taxCode !== CBOmissionType.OMISSION_DASH ? taxCode : (form.cbInvoicingAddress.invoicingCompanyFiscalCode == CBOmissionType.OMISSION_DASH ? null : form.cbInvoicingAddress.invoicingCompanyFiscalCode),
                recipientCode: isFranceInvoicingCountry ? undefined : (form.cbInvoicingAddress.invoicingRecipientCode == CBOmissionType.OMISSION_DASH ? null : form.cbInvoicingAddress.invoicingRecipientCode),
                addressType: CBAddressType.INVOICING
            });
    }

    const salesAreas: any[] = form.salesAreas?.filter((area: any) => !!area.distributionChannel && !!area.division)
        .map((sa: any) => ({
        id: sa.id || null,
        salesOrganization: sa.salesOrganization == CBOmissionType.OMISSION_DASH ? null : sa.salesOrganization || null,
        distributionChannel: sa.distributionChannel == CBOmissionType.OMISSION_DASH ? null : sa.distributionChannel || null,
        division: sa.division || null,
        currency: sa.currency || null
    })) || [];

    const payments: any[] = form.cbPaymentInfo?.filter((pay: any) => Object.values(pay).some((value: any) => !!value))
        .map(p => ({
            id: p.id || null,
            buagId: p.buagId || null,
            buagClass: p.buagClass == CBOmissionType.OMISSION_DASH ? null : p.buagClass || null,
            iban: p.iban == CBOmissionType.OMISSION_DASH ? null : p.iban?.toUpperCase() || null,
            accountHolder: p.accountHolder == CBOmissionType.OMISSION_DASH ? null : p.accountHolder || null,
            referenceDetails: p.referenceDetails == CBOmissionType.OMISSION_DASH ? null : p.referenceDetails || null,
            paymentMethod: p.paymentMethod == CBOmissionType.OMISSION_DASH ? null : p.paymentMethod || null,
            paymentTerms: p.paymentTerms == CBOmissionType.OMISSION_DASH ? null : p.paymentTerms || null,
            correspondingVariant: p.correspondingVariant == CBOmissionType.OMISSION_DASH ? null : p.correspondingVariant || null
        })) || [];

    
    var contactPersons: any[] = [];

    if(isOrganization) {
        contactPersons = [...form.cbCorporateInfo?.contactPersons?.filter((cp: any) => !!cp.name && !!cp.surname)
            .map((cp: any) => ({
                id: cp.id || null,
                //cbCustomerId: cp.cbCustomerId || null,
                bpCode: cp.bpCode == CBOmissionType.OMISSION_DASH ? null : cp.bpCode || null,
                name: cp.name == CBOmissionType.OMISSION_DASH ? null : cp.name || null,
                surname: cp.surname == CBOmissionType.OMISSION_DASH ? null : cp.surname || null,
                address: cp.address == CBOmissionType.OMISSION_DASH ? null : cp.address || null,
                houseNumber: cp.houseNumber == CBOmissionType.OMISSION_DASH ? null : cp.houseNumber || null,
                country: cp.country == CBOmissionType.OMISSION_DASH ? null : cp.country || null,
                region: cp.region == CBOmissionType.OMISSION_DASH ? null : cp.region || null, 
                city: cp.city == CBOmissionType.OMISSION_DASH ? null : cp.city || null,
                postalCode: cp.postalCode == CBOmissionType.OMISSION_DASH ? null : cp.postalCode || null,
                phoneNumber: cp.phoneNumber == CBOmissionType.OMISSION_DASH ? null : cp.phoneNumber || null,
                mobile: cp.mobile == CBOmissionType.OMISSION_DASH ? null : cp.mobile || null,
                email: cp.email == CBOmissionType.OMISSION_DASH ? null : cp.email || null,
                delete: cp.delete == CBOmissionType.OMISSION_DASH ? null : cp.delete || null
            })) || [], 
            ...contactPersonToDeleteArray.map(cptd => ({
                id: cptd.id || null,
                //cbCustomerId: cp.cbCustomerId || null,
                bpCode: cptd.bpCode == CBOmissionType.OMISSION_DASH ? null : cptd.bpCode || null,
                name: cptd.name == CBOmissionType.OMISSION_DASH ? null : cptd.name || null,
                surname: cptd.surname == CBOmissionType.OMISSION_DASH ? null : cptd.surname || null,
                address: cptd.address == CBOmissionType.OMISSION_DASH ? null : cptd.address || null,
                houseNumber: cptd.houseNumber == CBOmissionType.OMISSION_DASH ? null : cptd.houseNumber || null,
                country: cptd.country == CBOmissionType.OMISSION_DASH ? null : cptd.country || null,
                region: cptd.region == CBOmissionType.OMISSION_DASH ? null : cptd.region || null, 
                city: cptd.city == CBOmissionType.OMISSION_DASH ? null : cptd.city || null,
                postalCode: cptd.postalCode == CBOmissionType.OMISSION_DASH ? null : cptd.postalCode || null,
                phoneNumber: cptd.phoneNumber == CBOmissionType.OMISSION_DASH ? null : cptd.phoneNumber || null,
                mobile: cptd.mobile == CBOmissionType.OMISSION_DASH ? null : cptd.mobile || null,
                email: cptd.email == CBOmissionType.OMISSION_DASH ? null : cptd.email || null,
                delete: cptd.delete
            }))
        ];
    }

    return {
        cbCustomerId: form.cbDetails.cbCustomerId,
        bpCode: form.cbDetails.bpCode == CBOmissionType.OMISSION_DASH ? undefined : form.cbDetails.bpCode,
        email: email || undefined,
        cbType: form.cbDetails.cbType || undefined,
        role: form.cbDetails.role || undefined,
        language: form.cbDetails.language || undefined,
        partnerType: form.cbDetails.partnerType || undefined,
        vip: form.cbDetails.vip == CBOmissionType.OMISSION_DASH ? undefined : form.cbDetails.vip || undefined,
        blacklist: form.cbDetails.blacklist || undefined,
        blacklistDescription: form.cbDetails.blacklistDescription == CBOmissionType.OMISSION_DASH ? undefined : form.cbDetails.blacklistDescription || undefined,
        initialRegDate: form.cbDetails.initialRegistrationDate || undefined,
        finalRegDate: form.cbDetails.finalRegistrationDate == CBOmissionType.OMISSION_DASH ? undefined : form.cbDetails.finalRegistrationDate || undefined,
        bpBlocked: form.cbDetails.bpBlocked == CBOmissionType.OMISSION_DASH ? undefined : form.cbDetails.bpBlocked || undefined,
        
        legalForm: form.cbPersonalInfo.legalForm == CBOmissionType.OMISSION_DASH ? undefined :  form.cbPersonalInfo.legalForm || undefined,
        publicAuthority: form.cbPersonalInfo.publicAuthority == CBOmissionType.OMISSION_DASH ? CBYesNoType.NO : form.cbPersonalInfo.publicAuthority || CBYesNoType.NO,
        paIdNumber: form.cbPersonalInfo.paIdNumber == CBOmissionType.OMISSION_DASH ? undefined : form.cbPersonalInfo.paIdNumber || undefined,
        dealerType: form.cbPersonalInfo.dealerType == CBOmissionType.OMISSION_DASH ? undefined :  form.cbPersonalInfo.dealerType || undefined,
        customerType: form.cbPersonalInfo.customerType == CBOmissionType.OMISSION_DASH ? undefined :  form.cbPersonalInfo.customerType || undefined,
        registry: {
            firstName: form.cbPersonalInfo.firstName == CBOmissionType.OMISSION_DASH ? undefined :  form.cbPersonalInfo.firstName || undefined,
            lastName: form.cbPersonalInfo.lastName == CBOmissionType.OMISSION_DASH ? undefined :  form.cbPersonalInfo.lastName || undefined,
            gender: form.cbPersonalInfo.gender == CBOmissionType.OMISSION_DASH ? undefined : form.cbPersonalInfo.gender || undefined,
            birthDate: form.cbPersonalInfo.birthDate == CBOmissionType.OMISSION_DASH ? undefined : form.cbPersonalInfo.birthDate || undefined,
            birthCountry: form.cbPersonalInfo.birthCountry == CBOmissionType.OMISSION_DASH ? undefined : form.cbPersonalInfo.birthCountry || undefined,
            birthProvince: form.cbPersonalInfo.birthProvince == CBOmissionType.OMISSION_DASH ? undefined : form.cbPersonalInfo.birthProvince || undefined,
            birthCity: form.cbPersonalInfo.birthCity == CBOmissionType.OMISSION_DASH ? undefined : form.cbPersonalInfo.birthCity || undefined,
            vatNumber: form.cbPersonalInfo.vatNumber == CBOmissionType.OMISSION_DASH ? undefined : form.cbPersonalInfo.vatNumber || undefined,
            vatNumberCountry: form.cbPersonalInfo.vatNumberCountry == CBOmissionType.OMISSION_DASH ? undefined : form.cbPersonalInfo.vatNumberCountry || undefined,
            businessName: form.cbPersonalInfo.businessName == CBOmissionType.OMISSION_DASH ? undefined : form.cbPersonalInfo.businessName || undefined,
            taxCode: form.cbPersonalInfo.taxCode == CBOmissionType.OMISSION_DASH ? undefined : form.cbPersonalInfo.taxCode || undefined,
            drvLicenceNumber: form.cbPersonalInfo.drvLicenceNumber == CBOmissionType.OMISSION_DASH ? undefined : form.cbPersonalInfo.drvLicenceNumber || undefined,
            siren: form.cbPersonalInfo.siren == CBOmissionType.OMISSION_DASH ? undefined : isFranceOrganization(form, false) ? form.cbPersonalInfo.siren || undefined : undefined,
            siret: form.cbPersonalInfo.siret == CBOmissionType.OMISSION_DASH ? undefined : isFranceOrganization(form, false) ? form.cbPersonalInfo.siret || undefined : undefined,
        },
        contactInfo: {
            prefix: mobilePrefix || null,
            mobile: mobile,
            addresses: addresses.length > 0 ? addresses : undefined
        },
        salesAreas: salesAreas.length > 0 ? salesAreas : undefined,
        paymentInfo: payments.length > 0 ? payments : undefined,
        corporateInfo: isOrganization ? {
            idZIT019: form.cbCorporateInfo.idZIT019 || undefined,
            salesArea: form.cbCorporateInfo.salesArea == CBOmissionType.OMISSION_DASH ? undefined : form.cbCorporateInfo.salesArea || undefined,
            fleet: form.cbCorporateInfo.fleet == CBOmissionType.OMISSION_DASH ? undefined : form.cbCorporateInfo.fleet || undefined,
            class: form.cbCorporateInfo.class == CBOmissionType.OMISSION_DASH ? undefined : form.cbCorporateInfo.class || undefined,
            sizingClass: form.cbCorporateInfo.sizingClass == CBOmissionType.OMISSION_DASH ? undefined : form.cbCorporateInfo.sizingClass || undefined,
            maxRentalNum: form.cbCorporateInfo.maxRentalNum == CBOmissionType.OMISSION_DASH ? undefined : form.cbCorporateInfo.maxRentalNum || undefined,
            contactPersons: contactPersons.length > 0 ? contactPersons : undefined 
        } : undefined,
        modifiedLinkedAddresses: extraAddressesDelta.length > 0 ? extraAddressesDelta : undefined,
    }
}

export const cbDateFormat = (date: string, format: "DD/MM/YYYY" | "DD-MM-YYYY") => {
    return dayjs(date).isValid() ? dayjs(date).format(format) : null;
}

export const isFranceOrganization = (customer: IFormValues| CustomerBaseDto, isDTO: boolean) => {

    if(isDTO){
        const dto = customer as CustomerBaseDto
        return dto?.partnerType == CBPartnerType.ORGANIZATION && dto?.registry?.vatNumberCountry == CBCountry.FRANCE;
    }
    const form = customer as IFormValues;
    return form?.cbDetails?.partnerType == CBPartnerType.ORGANIZATION && form?.cbPersonalInfo?.vatNumberCountry == CBCountry.FRANCE;
}

export const getSalesOrganizationKeyByTenants = (tenants: any[]): string => {

    if(tenants?.length == 1) {
        switch (tenants[0].name) {
            case CBTenants.ITALY:
                return CBCountryExtended.ITALY;

            case CBTenants.FRANCE:
                return CBCountryExtended.FRANCE;

            default:
                return '';
        }
    }
    else return '';
}

export const filterSalesOrganizationByTenants = (salesAreaCountry: string, tenants: any[]) => {

    switch (salesAreaCountry) {
        case CBCountryExtended.ITALY:
            return tenants?.map(t => t?.name).includes(CBTenants.ITALY);
        case CBCountryExtended.FRANCE:
            return tenants?.map(t => t?.name).includes(CBTenants.FRANCE);
        default:
            return false;
    }
}

export const getCountriesByTenants = (tenants: any[]): string[] => {

    let countries: string[] = [];
    tenants?.forEach(t => {
        switch (t.name) {
            case CBTenants.ITALY:
                countries.push(CBCountryExtended.ITALY);
                break;

            case CBTenants.FRANCE:
                countries.push(CBCountryExtended.FRANCE);
                break;
        }
    })

    return countries;
}

export const getCompanyCodeByTenant = (tenant: string) => {

    switch (tenant) {
        case CBTenants.ITALY:
            return CBConsentCompanyCode.DRIVALIA_ITALY;

        case CBTenants.FRANCE:
            return CBConsentCompanyCode.DRIVALIA_FRANCE;
        default:
            return null;
    }
}

export const getCountryByCode = (code: string) => {

    switch(code) {
        case CBConsentCompanyCode.DRIVALIA_ITALY:
        case CBCountryExtended.ITALY:
            return CBCountry.ITALY;

        case CBConsentCompanyCode.DRIVALIA_FRANCE:
        case CBCountryExtended.FRANCE:
            return CBCountry.FRANCE;

        default:
            return code;
    }

}


